import React, {useEffect, useState} from 'react';
import {
    Box,
    Checkbox,
    Collapse,
    IconButton,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import {KeyboardArrowUp, KeyboardArrowDown, ErrorOutline} from '@mui/icons-material';
import {SampleFilesTable} from './SampleFilesTable';
import {isValidValue} from '../step3/ParseCsv';

const SampleMetadataTable = ({sample, experiment}: {sample: any; experiment: any}) => {
    return (
        <Table size="small" data-testid="sample-metadata-table" sx={{mt: 1}}>
            <TableHead>
                <TableRow sx={{bgcolor: 'surface-container-high'}}>
                    <TableCell>Sample ID</TableCell>
                    <TableCell>Seq-end</TableCell>
                    <TableCell>Sense</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                <TableRow sx={{bgcolor: 'surface-container-low'}}>
                    <TableCell>{'S-' + sample?.sample_num}</TableCell>
                    <TableCell>{experiment?.sequencing_read_type || sample?.files?.[0]?.strandedness_seq_end}</TableCell>
                    <TableCell>{experiment?.sequencing_sense || 'unstranded'}</TableCell>
                </TableRow>
            </TableBody>
        </Table>
    );
};

interface RowProps {
    sample: any;
    setSuccess: (success: boolean) => void;
    globalValues: any;
    errors: any;
    exp: any;
    selected: boolean;
    selectRow: (id: string) => void;
    fetchSamples: () => void;
    editSample: (sample: any) => void;
}

/**
 * Row representing one sample.
 */
export default function Row({sample, setSuccess, globalValues, errors, exp, selected, selectRow, fetchSamples, editSample}: RowProps) {
    const [editState, setEditState] = useState<{[key: string]: string}>({});

    // Start open state with the correct value
    const errorInSample = Object.keys(errors).some((m: any) => errors[m].includes(sample.id));
    const [open, setOpen] = useState(errorInSample);
    // TODO: Can I just get rid of this? If an error is fixed, dont we reFetchSamples? That is completely new samples so completely new rows.
    // Explicitly update open state when the errors change
    useEffect(() => {
        setOpen(errorInSample);
    }, [errors]);

    const errorMessages: Record<string, string> = {
        missingFiles: `This sample is missing files. Add (${exp?.files_per_sample - sample?.files.length}) files to resolve this issue.`,
        duplicateName:
            'This sample has the same name as another sample in your experiment. Please give this sample a unique name or delete it.',
        fileStatus: 'There was an error uploading your file. Please try uploading it again.'
    };

    const handleChange = (identifier: string, newValue: string) => {
        setEditState({[identifier]: newValue});
    };

    const handleSaveChanges = (key: any) => {
        if (editState[key] !== sample[key] && editState[key] !== sample.other_categories[key]) {
            if (key === 'name') {
                const newName = editState.name.trim();
                if (!newName || !isValidValue(newName)) {
                    setEditState({});
                    return;
                } else {
                    sample.name = newName;
                }
            } else {
                sample.other_categories[key] = editState[key];
            }
            editSample(sample);
            setEditState({});
            setSuccess(true);
        }
    };

    return (
        <>
            {/* Error Messages Box for Row with Errors */}
            <TableRow>
                <TableCell colSpan={8}>
                    {Object.keys(errors).map(
                        (m: any) =>
                            errors[m].includes(sample.id) && (
                                <Box
                                    key={m}
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        my: '1px',
                                        borderRadius: '4px',
                                        bgcolor: 'error-container',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'flex-start'
                                    }}>
                                    <Typography display={'flex'} alignItems={'center'} gap={2}>
                                        <ErrorOutline fontSize="small" /> {errorMessages[m]}
                                    </Typography>
                                </Box>
                            )
                    )}
                </TableCell>
            </TableRow>

            {/* Row with sample's name and categories for editing */}
            <TableRow>
                <TableCell align="right" padding="checkbox">
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell padding="checkbox">
                    <Checkbox checked={selected} onClick={() => selectRow(sample.id)} size="small" />
                </TableCell>
                <TableCell>
                    <TextField
                        sx={{minWidth: '140px'}}
                        value={editState.name !== undefined ? editState.name : sample.name}
                        onChange={e => handleChange('name', e.target.value)}
                        onBlur={() => handleSaveChanges('name')}
                    />
                </TableCell>
                {globalValues &&
                    Object.entries(globalValues)?.map(([key, options]) => {
                        return (
                            <TableCell key={key}>
                                <Select
                                    sx={{height: '30px', padding: '1px 2px'}}
                                    fullWidth
                                    value={editState[key] ? editState[key] : sample.other_categories[key]}
                                    onChange={e => handleChange(key, e.target.value)}
                                    onBlur={() => handleSaveChanges(key)}>
                                    {(options as string[])?.sort().map((option: string) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </TableCell>
                        );
                    })}
            </TableRow>

            {/* Row with nested tables for further metadata and files */}
            <TableRow>
                <TableCell sx={{p: '5px'}} colSpan={5 + (!globalValues ? 2 : Object.keys(globalValues)?.length)}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <SampleMetadataTable sample={sample} experiment={exp} />
                        <SampleFilesTable
                            sample={sample}
                            experiment={exp}
                            onUploadSuccess={() => {
                                setSuccess(true);
                                fetchSamples();
                            }}
                            onFileMetaEdited={() => fetchSamples()}
                        />
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}
